import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { Language, Translations, translations } from "../../i18n/translations";
import { useAuth } from "./AuthContext";
import { saveUserPreference } from "../userPreferences";

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  translate: (key: keyof Translations) => string;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: "en",
  setLanguage: () => {},
  translate: (key) => translations.en[key],
});

function detectBrowserLanguage(): Language {
  try {
    const langs = navigator.languages?.length
      ? navigator.languages
      : [navigator.language];
    for (const lang of langs) {
      const code = lang.split("-")[0].toLowerCase();
      if (code in translations) return code as Language;
    }
  } catch {}
  return "en";
}

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();

  const [language, setLanguageState] = useState<Language>(() => {
    try {
      const stored = localStorage.getItem("fatespin_lang");
      if (stored && stored in translations) return stored as Language;
    } catch {}
    return detectBrowserLanguage();
  });

  const syncedUserIdRef = useRef<string | undefined>(undefined);

  // Apply cloud preference on login
  useEffect(() => {
    if (!user || user.id === syncedUserIdRef.current) return;
    syncedUserIdRef.current = user.id;
    const cloudLang = user.user_metadata?.pref_language as string | undefined;
    if (cloudLang && cloudLang in translations) {
      setLanguageState(cloudLang as Language);
      try { localStorage.setItem("fatespin_lang", cloudLang); } catch {}
    }
  }, [user]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try { localStorage.setItem("fatespin_lang", lang); } catch {}
    if (user) saveUserPreference("pref_language", lang);
  }, [user]);

  const translate = useCallback(
    (key: keyof Translations) => translations[language][key],
    [language],
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, translate }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
